import { useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

/**
 * Fits its content inside the space it is given, so the Auth shell never
 * scrolls or clips at any window size. The content lays out at its natural
 * size; when that is taller (or wider) than the box, it is scaled down
 * uniformly around its centre. At normal sizes the scale stays at 1.
 * A ResizeObserver re-fits on window resizes and on content changes (e.g.
 * passkey enrollment moving between steps).
 */
export function AuthFit({ className, children }: { className?: string; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const outer = box.current;
    const content = inner.current;
    if (!outer || !content) return;

    const fit = () => {
      // offset*/scroll* sizes ignore transforms, so these are the natural size.
      const h = content.offsetHeight;
      const w = content.scrollWidth;
      const scale = Math.min(1, outer.clientHeight / h, outer.clientWidth / w);
      content.style.transform = scale < 1 ? `scale(${scale})` : "";
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(outer);
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={box} className={cn("auth-fit", className)}>
      <div ref={inner} className="auth-fit__inner">
        {children}
      </div>
    </div>
  );
}
